import LoaderIcon from '~/components/icons/Loader'
import { classNames } from '~/helpers/string'

type Variant = 'filled' | 'outlined' | 'text'
type Color = 'primary' | 'secondary' | 'error' | 'gray'
type Size = 'sm' | 'md' | 'lg'

export interface ButtonProps extends React.ComponentProps<'button'> {
  startIcon?: React.ReactElement
  endIcon?: React.ReactElement
  color?: Color
  variant?: Variant
  size?: Size
  fullWidth?: boolean
  className?: string
  loading?: boolean
}

function Button(props: ButtonProps) {
  const {
    variant = 'filled',
    color = 'primary',
    size = 'md',
    startIcon = null,
    endIcon = null,
    children,
    disabled,
    fullWidth,
    className: customClassNames = '',
    loading = false,
    type = 'button',
    ...restProps
  } = props

  const classes = classNames(
    disabled || loading ? 'pointer-events-none select-none opacity-40' : '',
    // sizing
    fullWidth ? 'w-full' : '',
    'py-2 px-5 inline-flex items-center justify-center gap-[10px] rounded-md',
    getButtonSize(size),
    // color
    'text-brand-violet-950 transition',
    getButtonColors(variant, color),
    // font
    'text-sm font-medium leading-5',
    customClassNames,
  )

  return (
    <button className={classes} type={type} {...restProps} disabled={disabled}>
      {startIcon ? loading ? <LoaderIcon /> : startIcon : null}
      {loading ? <LoaderIcon /> : null}
      <div className={loading ? 'invisible' : ''}>{children}</div>
      {endIcon ? loading ? <LoaderIcon /> : endIcon : null}
    </button>
  )
}

const getButtonSize = (size: Size) => {
  const sizeMap: Record<Size, string> = {
    sm: 'h-8',
    md: 'h-10',
    lg: 'h-12',
  }
  return sizeMap[size]
}

const getButtonColors = (variant: Variant, color: Color) => {
  const variantColorMap: Record<Variant, Record<Color, string>> = {
    filled: {
      primary: 'bg-brand-shamrock-400 hover:bg-brand-shamrock-500 active:bg-brand-shamrock-600',
      secondary: 'bg-brand-primary-200 hover:bg-brand-primary-300 active:bg-brand-primary-400',
      gray: '',
      error: '',
    },
    outlined: {
      primary: 'border border-brand-shamrock-400 hover:bg-brand-shamrock-400 active:bg-brand-shamrock-500',
      secondary: '',
      error: 'border border-brand-actions-error',
      gray: 'border border-brand-neutral-300 hover:bg-brand-gray-100 active:bg-brand-gray-300',
    },
    text: {
      primary: 'hover:bg-brand-primary-50 active:bg-brand-primary-200',
      secondary: 'hover:bg-brand-primary-50 active:bg-brand-primary-200',
      gray: '',
      error: '',
    },
  }
  const styles = variantColorMap[variant][color]
  if (styles.length === 0) throw new Error('Button variant/color not implemented')

  return styles
}

export default Button
